<!--
  @component CreatorEditor
  @description Editable list of creators with type selection, add/remove functionality
-->
<script lang="ts">
  import { t } from '@/i18n';
  import { getCreatorTypesForItemType, getDefaultCreatorType } from '@/data';
  import type { Creator, CreatorType } from '@/types';

  export let creators: Creator[] = [];
  export let itemType: string;

  // Get valid creator types for the current item type
  $: validCreatorTypes = getCreatorTypesForItemType(itemType);

  // Get creator type label
  function getCreatorTypeLabel(creatorType: CreatorType): string {
    return $t(`item.creatorTypes.${creatorType}`) || creatorType;
  }

  // Add a new creator
  function addCreator() {
    const defaultType = getDefaultCreatorType(itemType);
    creators = [...creators, { creatorType: defaultType, firstName: '', lastName: '' }];
  }

  // Remove a creator by index
  function removeCreator(index: number) {
    if (creators.length <= 1) return;
    creators = creators.filter((_, i) => i !== index);
  }
</script>

<div class="creators-section">
  <span class="field-label">{$t('item.fields.creators')}</span>

  {#each creators as creator, index (index)}
    <div class="creator-row">
      <div class="creator-header">
        <select
          class="field-select creator-type-select"
          bind:value={creator.creatorType}
          aria-label={$t('item.creatorTypes.author')}
        >
          {#each validCreatorTypes as creatorType}
            <option value={creatorType}>{getCreatorTypeLabel(creatorType)}</option>
          {/each}
        </select>
        {#if creators.length > 1}
          <button
            type="button"
            class="creator-remove"
            on:click={() => removeCreator(index)}
            title={$t('item.removeCreator')}
            aria-label={$t('item.removeCreator')}>×</button
          >
        {/if}
      </div>
      <div class="creator-inputs">
        <input
          class="field-input"
          placeholder={$t('item.firstName')}
          bind:value={creator.firstName}
          aria-label={$t('item.firstName')}
        />
        <input
          class="field-input"
          placeholder={$t('item.lastName')}
          bind:value={creator.lastName}
          aria-label={$t('item.lastName')}
        />
      </div>
    </div>
  {/each}

  <button type="button" class="creator-add" on:click={addCreator}>
    + {$t('item.addCreator')}
  </button>
</div>

<style>
  .creators-section {
    margin-bottom: 16px;
  }

  .field-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-secondary, #bbb);
    margin-bottom: 8px;
  }

  .creator-row {
    margin-bottom: 12px;
    padding: 12px;
    background: var(--color-bg-primary, #2b2b2b);
    border-radius: 6px;
    border: 1px solid var(--color-border-light, #444);
  }

  .creator-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    gap: 8px;
  }

  .creator-type-select {
    flex: 1;
  }

  .field-select {
    width: 100%;
    padding: 8px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .field-select:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .creator-inputs {
    display: flex;
    gap: 8px;
  }

  .field-input {
    flex: 1;
    padding: 8px;
    background: var(--color-bg-primary, #2b2b2b);
    border: 1px solid var(--color-border, #555);
    border-radius: 4px;
    color: var(--color-text-primary, #e8e8e8);
    font-size: 13px;
    box-sizing: border-box;
    transition: border-color 0.2s;
  }

  .field-input:focus {
    outline: none;
    border-color: var(--color-accent, #5c7cfa);
  }

  .creator-remove {
    background: var(--color-danger, #dc3545);
    border: none;
    color: white;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.2s;
  }

  .creator-remove:hover {
    background: var(--color-danger-hover, #c82333);
  }

  .creator-add {
    background: var(--color-success, #28a745);
    border: none;
    color: white;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    margin-top: 4px;
    transition: background-color 0.2s;
  }

  .creator-add:hover {
    background: #218838;
  }
</style>
